// referenceClassificationFloor.js
// Shared logic for syncing a block's classification level to its most-classified citation.
// A block can never be classified lower than the most-classified reference mark attached to
// it, so the effective level is always:
//
//   effectiveLevel = manualBaseline ? max(manualBaseline, citationsFloor) : citationsFloor
//
// where `manualBaseline` is `node.attrs.manualClassificationLevel` — the level the user
// explicitly chose via the Classification menu, persisted directly on the node (see
// classificationGutterPlugin.js, which writes it via setNodeManualClassificationBaseline) —
// or `null` if the user never touched classification for this block. There is no separate
// in-memory cache: the node attr is the single source of truth, read fresh every time, so a
// block created after mount (split, paste, etc.) behaves identically to one present at mount —
// both simply have `manualClassificationLevel: null` until the user explicitly picks a level.
// `citationsFloor` is likewise recomputed fresh from the document + Y.Map every time
// (getBlockCitationsMaxLevel), so removing the last above-(U) citation from a never-manually-
// classified block correctly falls all the way back to (U) (or the next-highest remaining
// citation), rather than getting stuck at whatever level a citation last raised it to.
//
// Extracted from snippetDropPlugin.js (MINSKY) so it can be reused by any code path that writes
// to Y.Map("referenceMarks") — not just drag-and-drop — via classificationFloorSyncPlugin's
// reactive Y.observer.

import {setNodeClassificationLevel} from './classificationHelpers';
import {getBlockCitationsMaxLevel, getMaxClassificationLevel} from './documentClassification';
import {syncDocumentHeaderClassification} from './classificationHeaderSync';
import useYDocStore from '../../../stores/useYDocStore';

/**
 * Find the paragraph/heading block that contains a text run carrying a `reference`
 * mark with the given uid. Returns the block's position (usable with
 * setNodeClassificationLevel/tr.doc.nodeAt), or null if not found.
 */
export function findBlockPosForReferenceUid(doc, uid) {
    let result = null;
    doc.descendants((node, pos) => {
        if (result != null) return false;
        if (node.type.name !== 'paragraph' && node.type.name !== 'heading') return true;

        let hasUid = false;
        node.descendants((child) => {
            if (hasUid) return false;
            if (child.isText && child.marks.some((m) => m.type.name === 'reference' && m.attrs?.uid === uid)) {
                hasUid = true;
            }
            return true;
        });

        if (hasUid) {
            result = pos;
            return false;
        }
        return false; // don't descend further into a non-matching paragraph/heading
    });
    return result;
}

/**
 * Sync the classification level of the block containing `referenceUid` to:
 *
 *   manualBaseline ? max(manualBaseline, citationsMaxLevel) : citationsMaxLevel
 *
 * `manualBaseline` is read directly from the node's persisted `manualClassificationLevel`
 * attr — `null`/absent means the user never explicitly chose a level for this block, in which
 * case the effective level is exactly the citations floor (so removing the last above-(U)
 * citation correctly falls back to (U), or the next-highest remaining citation). Only
 * classificationGutterPlugin.js's menu-apply callback ever writes that attr (via
 * setNodeManualClassificationBaseline) — this function only ever reads it.
 *
 * Also syncs the document header's aggregate classification when the level actually changes.
 *
 * Safe to call from any context that just wrote a reference into Y.Map("referenceMarks"):
 * it dispatches a plain ProseMirror transaction rather than writing back to `yReferenceMarks`,
 * so it cannot trigger observer feedback loops.
 *
 * @param {import('prosemirror-view').EditorView} view
 * @param {string} referenceUid - uid of the reference mark that was just added to/removed from
 * @param {string} documentId - report/document id, for the Firestore header sync
 * @param {import('yjs').Map} [yReferenceMarks] - Y.Map("referenceMarks"), if the caller
 *   already has a direct reference to it (e.g. classificationFloorSyncPlugin, which is
 *   handed it explicitly to sidestep the useYDocStore initialization-ordering hazard — see
 *   that plugin's comment). Falls back to useYDocStore.getState().yReferenceMarks when
 *   omitted, which is safe for callers that only run well after the store is populated
 *   (e.g. snippetDropPlugin.js's drag-and-drop handlers, triggered by real user interaction).
 */
export function syncBlockClassificationFloorForReference(view, referenceUid, documentId, yReferenceMarks) {
    try {
        const pos = findBlockPosForReferenceUid(view.state.doc, referenceUid);
        if (pos == null) return;

        const node = view.state.doc.nodeAt(pos);
        if (!node || (node.type.name !== 'paragraph' && node.type.name !== 'heading')) return;

        const {yReferenceMarks: storeYReferenceMarks, yDocMeta, ydoc} = useYDocStore.getState();
        const marks = yReferenceMarks || storeYReferenceMarks;

        const currentLevel = node.attrs.classificationLevel || 'U';
        const manualBaseline = node.attrs.manualClassificationLevel || null;

        const citationsFloor = getBlockCitationsMaxLevel(node, marks);
        const newLevel = manualBaseline
            ? getMaxClassificationLevel(manualBaseline, citationsFloor)
            : citationsFloor;

        if (newLevel === currentLevel) return;

        const tr = view.state.tr;
        setNodeClassificationLevel(tr, pos, newLevel);
        view.dispatch(tr);

        syncDocumentHeaderClassification({
            documentId,
            yDocMeta,
            ydoc,
            committedDoc: view.state.doc,
            appliedLevel: newLevel,
        });
    } catch (err) {
        console.error('[referenceClassificationFloor] Failed to sync block classification floor:', err);
    }
}
